import { useEffect, useState } from "react";
import { useAppSelector } from "./redux";
import type { IAnalyticsController } from "../hexagon/adapters/driver/analyticsController";
import { isMobile } from "../util/layout";
import {
  shouldExcludePath,
  type IntercomTriggerOptions,
} from "../util/intercomTrigger";

interface IUseIntercom {
  webIntercomProvider: IWebIntercomProvider;
  circadiaEngineProvider: ICircadiaEngineProvider;
  analyticsController: IAnalyticsController;
}

export const useIntercom = ({ webIntercomProvider }: IUseIntercom) => {
  const [toggle, setToggle] = useState(false);

  const { user, userClinic } = useAppSelector((state) => ({
    user: state.auth.user,
    userClinic: state.clinic.userClinic,
  }));

  useEffect(() => {
    webIntercomProvider.boot({
      hide_default_launcher: false,
      name: user?.fullName || undefined,
      facility: userClinic?.name || undefined,
      email: user?.email || undefined,
      user_id: user?.userID || undefined,
    });

    // Global event listener for intercom triggers
    const handleIntercomEvent = (event: Event) => {
      // Don't trigger on mobile
      if (isMobile()) return;

      const customEvent = event as CustomEvent<IntercomTriggerOptions>;
      const options = customEvent.detail || {};
      const { delay = 0, reason = "manual", excludePaths = [] } = options;

      // Check if current path should be excluded
      if (shouldExcludePath(excludePaths)) {
        return;
      }

      // Log the trigger reason for analytics
      console.log(`Intercom triggered via global event: ${reason}`);

      if (delay > 0) {
        setTimeout(() => {
          handleToggle();
        }, delay);
      } else {
        handleToggle();
      }
    };

    // Add global event listener
    window.addEventListener("openIntercom", handleIntercomEvent);

    return () => {
      webIntercomProvider.shutdown();
      window.removeEventListener("openIntercom", handleIntercomEvent);
    };
  }, []);

  const handleWebToggle = () => {
    if (toggle === true) {
      webIntercomProvider.hide();
      setToggle(false);
    } else {
      setToggle(true);
      webIntercomProvider.show();
    }
  };

  const handleToggle = async () => {
    handleWebToggle();
  };

  webIntercomProvider.onHide(() => {
    setToggle(false);
  });

  webIntercomProvider.onShow(() => {
    setToggle(true);
  });

  return { handleToggle };
};
